// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect } from "react";
import { Block, NewsBlocksProps } from "../../../types/NewsTypes";
import { Divider, Form } from "antd";
import { NewsBlock } from "./NewsBlock";
import { NewsAddBlocButton } from "./NewsAddBlocButton";
import { useTranslation } from "react-i18next";
import { requestGetTemplates } from "../../../../state/template/TemplateEffects";
import { setTemplates } from "../../../../state/template/TemplateEvents";
import { displayError } from "../../../../utils/displayError";

export const NewsBlocks: React.FunctionComponent<NewsBlocksProps> = (
  props: NewsBlocksProps
) => {
  const { t } = useTranslation();
  const { blocks, setBlocks, editMode } = props;

  useEffect(() => {
    requestGetTemplates();
  }, []);

  useEffect(() => {
    return requestGetTemplates.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setTemplates(result.data);
      } else {
        displayError("templates.load_failed");
      }
    });
  });

  const handleAddBlock = (block: Block, index?: number) => {
    if (index !== undefined) {
      const newBlocs = [...blocks];
      newBlocs.splice(index, 0, block);
      setBlocks(newBlocs);
    } else {
      setBlocks([...blocks, block]);
    }
  };

  const handleUpdateBlock = (block: Block, index: number) => {
    const newBlocs = [...blocks];
    newBlocs[index] = block;
    setBlocks(newBlocs); //
  };

  const handleDeleteBlock = (index: number) => {
    const newBlocs = [...blocks];
    newBlocs.splice(index, 1);
    setBlocks(newBlocs);
  };

  const handleAscendBlock = (index: number) => {
    const block = blocks[index];
    const newBlocs = [...blocks];
    newBlocs.splice(index, 1);
    newBlocs.splice(index - 1, 0, block);
    setBlocks(newBlocs);
  };

  const handleDescendBlock = (index: number) => {
    const block = blocks[index];
    const newBlocs = [...blocks];
    newBlocs.splice(index, 1);
    newBlocs.splice(index + 1, 0, block);
    setBlocks(newBlocs);
  };

  return (
    <>
      <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
        <div className="flex-grow-1 d-flex align-items-center">
          <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
            <h3 className="text-secondary mb-0">{t("news.news_blocks")}</h3>
          </Divider>
        </div>
        {editMode && blocks.length < 1 && (
          <div className="d-flex align-items-center divider-sm-transparent">
            <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
              <Form.Item className="mb-0">
                <NewsAddBlocButton
                  buttonLabel={t("news.global_buttons.block.add")}
                  handleAddBlock={handleAddBlock}
                />
              </Form.Item>
            </Divider>
          </div>
        )}
      </div>
      {editMode && blocks.length < 1 && (
        <div className="mb-4 text-center">{t("news.no_blocks")}</div>
      )}
      {blocks.map((block: Block, index) => (
        <React.Fragment key={block.idTempLocal}>
          <Form.Item className="mb-0">
            <NewsBlock
              indexBlock={index}
              block={block}
              handleUpdateBlock={handleUpdateBlock}
              handleDeleteBlock={handleDeleteBlock}
              handleAddBlock={handleAddBlock}
              handleAscendBlock={
                blocks.length > 1 && index > 0 ? handleAscendBlock : undefined
              }
              handleDescendBlock={
                blocks.length > 1 && index < blocks.length - 1
                  ? handleDescendBlock
                  : undefined
              }
              editMode={editMode}
            />
          </Form.Item>
        </React.Fragment>
      ))}
      {editMode && blocks.length >= 1 && (
        <div className="d-flex justify-content-center">
          <NewsAddBlocButton
            buttonLabel={t("news.global_buttons.block.add")}
            handleAddBlock={handleAddBlock}
          />
        </div>
      )}
    </>
  );
};

NewsBlocks.defaultProps = {
  editMode: true
};
